/**
 * Raw PCM16 tap on a microphone stream — what the live-transcript leg streams.
 *
 * An AudioWorklet on the audio thread resamples the mic's Float32 samples to
 * `PCM_SAMPLE_RATE` (16 kHz — the rate speech models are built for, and 3–5×
 * less uplink than the 44.1/48 kHz the device runs at; anti-aliased) and folds them into
 * fixed-length frames of 16-bit little-endian mono PCM, posting each one to the
 * main thread. A stereo mic is downmixed to mono by the node's channel settings.
 *
 * Two-step start, on purpose: `createPcm16AudioContext()` is SYNCHRONOUS so the
 * caller can run it inside the click that starts the recording — iOS Safari
 * only lets a context start under user activation, and the mic prompt that
 * follows can outlive that activation. `startPcm16Capture()` then wires the
 * (already granted) stream into it.
 *
 * Runs beside `MediaRecorder` on the same `MediaStream`: the recorder keeps
 * producing the compact container file for storage; this tap only feeds the
 * live socket.
 */

/** Output rate of every frame, whatever the device's native rate. */
export const PCM_SAMPLE_RATE = 16_000;

const WORKLET_NAME = "pcm16-capture";
/** How long a suspended context may take to start before the tap is declared unavailable. */
const RESUME_TIMEOUT_MS = 3_000;
/** How long `stop()` waits for the worklet to hand over its partial frame. */
const FLUSH_TIMEOUT_MS = 300;

interface BiquadStage {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface ResampleState {
  /** Input samples per output sample. */
  ratio: number;
  /** Fractional read position for the next output sample, in input samples from the start of the next block. */
  pos: number;
  /** Last (filtered) input sample of the previous block — interpolation across block edges. */
  last: number;
  /** Anti-alias low-pass stages, empty when not decimating. */
  stages: BiquadStage[];
  /** Reused per call (audio-thread friendly): filtered input, and the output view. */
  scratch: Float32Array;
  out: Int16Array;
}

/**
 * Pure. Builds the resampler state for `inputRate` → `outputRate`. When
 * decimating, a 6th-order Butterworth low-pass (three cascaded biquads, RBJ
 * cookbook) at 0.4375 × outputRate (7 kHz for 16 kHz) keeps out-of-band noise
 * from folding into the speech band.
 *
 * SELF-CONTAINED BY DESIGN — inlined into the worklet via `toString()`; it
 * must reference nothing outside its own body.
 */
export function createResampleState(
  inputRate: number,
  outputRate: number,
): ResampleState {
  const ratio = inputRate / outputRate;
  const stages: BiquadStage[] = [];
  if (ratio > 1) {
    const w0 = (2 * Math.PI * 0.4375 * outputRate) / inputRate;
    const cos = Math.cos(w0);
    const sin = Math.sin(w0);
    for (const q of [0.5176, 0.7071, 1.9319]) {
      const alpha = sin / (2 * q);
      const a0 = 1 + alpha;
      stages.push({
        b0: (1 - cos) / 2 / a0,
        b1: (1 - cos) / a0,
        b2: (1 - cos) / 2 / a0,
        a1: (-2 * cos) / a0,
        a2: (1 - alpha) / a0,
        x1: 0,
        x2: 0,
        y1: 0,
        y2: 0,
      });
    }
  }
  return {
    ratio,
    pos: 0,
    last: 0,
    stages,
    scratch: new Float32Array(0),
    out: new Int16Array(0),
  };
}

/**
 * Low-passes (when decimating) and linearly interpolates Float32 audio down
 * to PCM16 at `state.ratio` input samples per output sample. `state` carries
 * the filter delays, the fractional position, and the previous block's last
 * sample so consecutive blocks join seamlessly. The returned view lives in
 * `state.out` and is valid until the next call — copy it before then. No
 * per-call allocation once the buffers have grown to the block size.
 *
 * SELF-CONTAINED BY DESIGN — inlined into the worklet via `toString()`; it
 * must reference nothing outside its own body.
 */
export function resampleToPcm16(
  input: Float32Array,
  state: ResampleState,
): Int16Array {
  const n = input.length;
  let x = input;
  if (state.stages.length > 0) {
    if (state.scratch.length < n) state.scratch = new Float32Array(n);
    const y = state.scratch.subarray(0, n);
    for (let i = 0; i < n; i++) {
      let v = input[i];
      for (let s = 0; s < state.stages.length; s++) {
        const st = state.stages[s];
        const out =
          st.b0 * v +
          st.b1 * st.x1 +
          st.b2 * st.x2 -
          st.a1 * st.y1 -
          st.a2 * st.y2;
        st.x2 = st.x1;
        st.x1 = v;
        st.y2 = st.y1;
        st.y1 = out;
        v = out;
      }
      y[i] = v;
    }
    x = y;
  }
  const ratio = state.ratio;
  const capacity = Math.ceil(n / ratio) + 1;
  if (state.out.length < capacity) state.out = new Int16Array(capacity);
  const out = state.out;
  let count = 0;
  let pos = state.pos;
  while (pos < n - 1) {
    const i = Math.floor(pos);
    const frac = pos - i;
    const s0 = i < 0 ? state.last : x[i];
    const s1 = x[i + 1];
    const v = Math.max(-1, Math.min(1, s0 + (s1 - s0) * frac));
    out[count++] = v < 0 ? v * 0x8000 : v * 0x7fff;
    pos += ratio;
  }
  state.pos = pos - n;
  state.last = n > 0 ? x[n - 1] : state.last;
  return out.subarray(0, count);
}

/**
 * Worklet source as text: the module must load from a URL, and a Blob URL keeps
 * this self-contained (no bundler worker plumbing). The resampler is inlined
 * from the function above so one implementation serves both the audio thread
 * and the unit tests.
 */
function workletSource(): string {
  return `
const createResampleState = ${createResampleState.toString()};
const resampleToPcm16 = ${resampleToPcm16.toString()};
class Pcm16Capture extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.frameSamples = options.processorOptions.frameSamples;
    this.state = createResampleState(sampleRate, options.processorOptions.targetRate);
    this.frame = new Int16Array(this.frameSamples);
    this.filled = 0;
    this.port.onmessage = (e) => {
      if (e.data !== "flush") return;
      if (this.filled > 0) {
        this.port.postMessage(this.frame.slice(0, this.filled).buffer);
        this.filled = 0;
      }
      this.port.postMessage("flushed");
    };
  }
  process(inputs) {
    const samples = inputs[0] && inputs[0][0];
    if (!samples) return true;
    const pcm = resampleToPcm16(samples, this.state);
    for (let i = 0; i < pcm.length; i++) {
      this.frame[this.filled++] = pcm[i];
      if (this.filled === this.frameSamples) {
        const full = this.frame.buffer;
        this.port.postMessage(full, [full]);
        this.frame = new Int16Array(this.frameSamples);
        this.filled = 0;
      }
    }
    return true;
  }
}
registerProcessor("${WORKLET_NAME}", Pcm16Capture);
`;
}

export interface Pcm16CaptureHandle {
  /** The rate the frames are sampled at — always `PCM_SAMPLE_RATE`. */
  readonly sampleRate: number;
  /** Flushes the partial frame, stops the tap, and closes the context; never stops the stream's tracks. */
  stop: () => Promise<void>;
}

export interface StartPcm16CaptureOptions {
  /** A context from `createPcm16AudioContext()`, created under user activation. */
  context: AudioContext;
  /** Frame length — the granularity frames reach `onFrame` (and the socket) at. */
  frameMs: number;
  /** One frame of PCM16 LE mono at `PCM_SAMPLE_RATE`, `frameMs` long (the last one may be shorter). The buffer is transferred: keep or send it, don't reuse it. */
  onFrame: (frame: ArrayBuffer) => void;
  /**
   * The context left or re-entered `running` while the tap was up (an iOS
   * call/Siri interruption, a backgrounded tab). Not called for the close
   * `stop()` performs. The tap itself retries `resume()`.
   */
  onStateChange?: (state: AudioContextState) => void;
}

/**
 * Create the tap's audio context — call SYNCHRONOUSLY inside the user gesture
 * that starts recording (before any `await`), or iOS Safari keeps it suspended.
 * Throws where AudioWorklet is unsupported; the caller treats that as "no live leg".
 */
export function createPcm16AudioContext(): AudioContext {
  if (
    typeof AudioContext === "undefined" ||
    typeof AudioWorkletNode === "undefined"
  ) {
    throw new Error("AudioWorklet is not supported in this browser");
  }
  const context = new AudioContext();
  // Kick the start while the gesture is still active; awaited (bounded) later.
  void context.resume().catch(() => undefined);
  return context;
}

export async function startPcm16Capture(
  stream: MediaStream,
  { context, frameMs, onFrame, onStateChange }: StartPcm16CaptureOptions,
): Promise<Pcm16CaptureHandle> {
  try {
    const moduleUrl = URL.createObjectURL(
      new Blob([workletSource()], { type: "application/javascript" }),
    );
    try {
      await context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    const frameSamples = Math.round((PCM_SAMPLE_RATE * frameMs) / 1000);
    const node = new AudioWorkletNode(context, WORKLET_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: "explicit",
      channelInterpretation: "speakers",
      processorOptions: { frameSamples, targetRate: PCM_SAMPLE_RATE },
    });
    let onFlushed: (() => void) | null = null;
    node.port.onmessage = (e: MessageEvent<ArrayBuffer | "flushed">) => {
      if (e.data === "flushed") onFlushed?.();
      else onFrame(e.data);
    };

    const source = context.createMediaStreamSource(stream);
    source.connect(node);
    // A node the graph never pulls is not processed on every engine; the
    // worklet writes no output, so wiring it to the destination is silent.
    node.connect(context.destination);

    if (context.state !== "running") {
      await Promise.race([
        context.resume(),
        new Promise<void>((resolve) => setTimeout(resolve, RESUME_TIMEOUT_MS)),
      ]);
    }
    if (context.state !== "running") {
      throw new Error(`audio context is ${context.state} (no user activation)`);
    }
    context.onstatechange = () => {
      const state = context.state;
      if (state === "closed") return;
      if (state !== "running") void context.resume().catch(() => undefined);
      onStateChange?.(state);
    };

    return {
      sampleRate: PCM_SAMPLE_RATE,
      async stop() {
        context.onstatechange = null;
        // Hand over the sub-frame tail before tearing down (bounded).
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, FLUSH_TIMEOUT_MS);
          onFlushed = () => {
            clearTimeout(timer);
            resolve();
          };
          node.port.postMessage("flush");
        });
        node.port.onmessage = null;
        source.disconnect();
        node.disconnect();
        await context.close();
      },
    };
  } catch (err) {
    // iOS Safari caps concurrent contexts — never leak one on a failed start.
    await context.close().catch(() => undefined);
    throw err;
  }
}
